import { useQuery } from "@tanstack/react-query";

import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_STATUS_LABELS,
  type TranslationFeedback,
  getDraftFeedback,
} from "@/api/feedback";

interface DraftFeedbackPanelProps {
  draftId: string;
  /** Hover'ом по карточке подсветить фрагмент в editor (Stage 2). */
  onHover?: (
    payload: {
      side: "source" | "target";
      offsetStart: number | null;
      offsetEnd: number | null;
    } | null,
  ) => void;
}

const STATUS_COLOR: Record<string, string> = {
  open: "var(--orange-100, #fff4e0)",
  in_progress: "var(--blue-100, #e6f1ff)",
  resolved: "var(--green-100, #e7f7ee)",
  wontfix: "var(--gray-100, #ebedf0)",
  orphaned: "var(--gray-100, #ebedf0)",
};

/**
 * Секция «Замечания» в правом te-rail. Список feedback'ов draft'а;
 * hover на карточку триггерит подсветку диапазона в editor.
 */
export function DraftFeedbackPanel({
  draftId,
  onHover,
}: DraftFeedbackPanelProps) {
  const q = useQuery({
    queryKey: ["draft-feedback", draftId],
    queryFn: async () => {
      const r = await getDraftFeedback(draftId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    refetchInterval: 30_000,
  });

  const items = q.data?.items ?? [];

  return (
    <div className="te-rail__sec">
      <div className="te-rail__head">
        <span className="te-rail__caret">▼</span>
        <span>Замечания</span>
        <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
          {items.length}
        </span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
      </div>
      <div className="te-rail__body">
        {q.isLoading ? (
          <div className="mt-empty">Загружаем…</div>
        ) : items.length === 0 ? (
          <div
            className="mt-text-tight mt-muted"
            style={{
              padding: "8px 0",
              textTransform: "none",
              letterSpacing: 0,
            }}
          >
            Нет замечаний. Выдели фрагмент в исходнике или переводе и
            нажми «Отправить» в попапе.
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: 6 }}
          >
            {items.map((it) => (
              <FeedbackCard key={it.feedback_id} item={it} onHover={onHover} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FeedbackCard({
  item,
  onHover,
}: {
  item: TranslationFeedback;
  onHover?: DraftFeedbackPanelProps["onHover"];
}) {
  const truncated =
    item.selected_text.length > 60
      ? item.selected_text.slice(0, 58) + "…"
      : item.selected_text;
  return (
    <div
      style={{
        padding: 8,
        background: STATUS_COLOR[item.status] ?? "var(--bg-soft, #f6f7f9)",
        border: "1px solid var(--border-default, #d0d5dd)",
        borderRadius: 6,
        fontSize: 12,
        cursor: onHover ? "pointer" : "default",
      }}
      onMouseEnter={() =>
        onHover?.({
          side: item.side,
          offsetStart: item.offset_start,
          offsetEnd: item.offset_end,
        })
      }
      onMouseLeave={() => onHover?.(null)}
    >
      <div
        style={{
          display: "flex",
          gap: 4,
          alignItems: "center",
          marginBottom: 4,
        }}
      >
        <span
          className="mt-badge mt-badge--neutral mt-badge--no-dot"
          style={{ fontSize: 9 }}
        >
          {item.side === "source" ? "ориг" : "перевод"}
        </span>
        <span style={{ fontSize: 10, color: "var(--text-soft)" }}>
          {FEEDBACK_STATUS_LABELS[item.status]}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: "var(--text-soft)" }}>
          {item.author}
        </span>
      </div>
      <div
        style={{
          fontStyle: "italic",
          color: "var(--text-soft)",
          marginBottom: 4,
        }}
      >
        «{truncated}»
      </div>
      {item.categories.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            marginBottom: 4,
          }}
        >
          {item.categories.map((c) => (
            <span
              key={c}
              className="mt-badge mt-badge--info mt-badge--no-dot"
              style={{ fontSize: 9 }}
            >
              {FEEDBACK_CATEGORY_LABELS[c]}
            </span>
          ))}
        </div>
      )}
      {item.comment_text && (
        <div style={{ color: "var(--text)" }}>{item.comment_text}</div>
      )}
    </div>
  );
}
